import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, normalizeProps as _normalizeProps, createBlock as _createBlock, normalizeClass as _normalizeClass, renderSlot as _renderSlot, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "tw" }
const _hoisted_2 = ["for"]
const _hoisted_3 = {
  key: 0,
  "aria-hidden": "true"
}
const _hoisted_4 = ["id", "aria-describedby", "autocomplete", "autocapitalize", "autocorrect", "disabled", "inputmode", "max", "maxlength", "min", "name", "pattern", "placeholder", "readonly", "required", "spellcheck", "type", "value", "data-currency"]
const _hoisted_5 = {
  key: 0,
  class: "pointer-events-none col-start-1 row-start-2 justify-self-start pl-4"
}

import { ref, toRef, computed, watch, onMounted, onUpdated, inject, useSlots } from "vue";
import SharedButtonOnlyIcon from "./SharedButtonOnlyIcon.vue";
import SvgIcon from "./SvgIcon/SvgIcon.vue";
import useInputValidation from "../composables/use-input-validation";
import type { HTMLInputElementEvent, InputEventDetails } from "../types/SharedInput.type";

const EMAIL_PATTERN = "^[\\w.+\\-]+@(?!xn--|\\.)[\\w.\\-]+(\\.[A-Za-z]{2,})$";

export default /*@__PURE__*/_defineComponent({
  __name: 'SharedNewTextfield',
  props: {
    autocomplete: {},
    autocapitalize: {},
    autocorrect: {},
    currency: {},
    dataTestName: { default: "" },
    disabled: { type: Boolean },
    displayName: {},
    forcedErrorMessage: {},
    hintPermanent: { type: Boolean },
    inputmode: {},
    label: {},
    max: {},
    maxlength: {},
    min: {},
    modelValue: {},
    name: {},
    pattern: {},
    patternErrorMessage: {},
    placeholder: {},
    readonly: { type: Boolean },
    required: { type: Boolean },
    requiredErrorMessage: {},
    requiredFlag: { type: Boolean, default: true },
    spellcheck: { type: Boolean },
    type: { default: "text" },
    validateOnChange: { type: Boolean },
    validateOnBlur: { type: Boolean },
    value: { default: "" }
  },
  emits: ["update:modelValue", "focus", "input", "change", "blur", "form-submit"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const emit = __emit;

const props = __props;

const inputRef = ref<HTMLInputElement | null>(null);
__expose({ inputRef });

const useVModel = computed(() => typeof props.modelValue !== "undefined");
const inputValue = useVModel.value
	? computed({
			get() {
				return String(props.modelValue ?? "");
			},
			set(val: string) {
				emit("update:modelValue", val);
			},
	  })
	: ref(props.value);
watch(
	() => props.value,
	(val) => {
		inputValue.value = val;
	}
);

/** Validation, Hint, Accessibility */

const UNIQUE_ID = `input-${Math.random().toString(36).slice(2, 9)}`;
const validationId = `validation-${UNIQUE_ID}`;
const { isValidationVisible, errorMessage, handleInput, handleChange, handleBlur } = useInputValidation(
	inputRef,
	props.displayName || props.name || "Feld",
	{
		patternErrorMessage: props.patternErrorMessage,
	},
	toRef(props, "forcedErrorMessage"),
	props.requiredErrorMessage
);

const hintId = `hint-${UNIQUE_ID}`;
const isHintVisible = computed(() => !!useSlots().default && (props.hintPermanent || !isValidationVisible.value));

const ariaDescribedBy = computed(() => {
	if (isHintVisible.value || isValidationVisible.value) {
		return `${isHintVisible.value ? hintId : ""} ${isValidationVisible.value ? validationId : ""}`.trim();
	}
	return undefined;
});

const dataTestErrorName = computed(() => (props.dataTestName ? `${props.dataTestName}-error` : ""));

/** Type Password */

const localType = ref(props.type);
const isPasswordVisible = ref(false);
const togglePassword = () => {
	isPasswordVisible.value = !isPasswordVisible.value;
	localType.value = isPasswordVisible.value ? "text" : "password";
};

/** Email Pattern */

// Hybris-RegEx für E-Mail, s. DefaultConfigValues.java: ^[\\w.+\\-]+@(?!xn--)[\\w.\\-]+(\\.[A-Za-z]{2,})$
// TUB-18168: ergänzt durch "." nach dem "@"
const localPattern = ref(props.pattern);
const _setLocalPattern = () => {
	localPattern.value = props.type === "email" && !props.pattern ? EMAIL_PATTERN : props.pattern;
};
onMounted(() => {
	_setLocalPattern();
});
onUpdated(() => {
	_setLocalPattern();
});

/** Events */

const onFocus = (e: FocusEvent) => {
	emit("focus", e as FocusEvent & { target: HTMLInputElement }, { errorMessage: errorMessage.value });
};

const onInput = (e: Event) => {
	const val = (e.target as HTMLInputElement).value;
	if (useVModel.value) {
		emit("update:modelValue", val);
	} else {
		inputValue.value = val;
	}

	handleInput(); // Validation
	emit("input", e as HTMLInputElementEvent, { errorMessage: errorMessage.value });
};

const onChange = (e: Event) => {
	if (!props.validateOnBlur) handleChange(props.validateOnChange); // Validation
	emit("change", e as HTMLInputElementEvent, { errorMessage: errorMessage.value });
};

const onBlur = (e: Event) => {
	if (props.validateOnBlur) handleBlur(props.validateOnBlur); // Validation
	emit("blur", e as HTMLInputElementEvent, { errorMessage: errorMessage.value });
};

/** Form Submit */

const formRef = inject("formRef", ref<HTMLFormElement | null>(null));
if (formRef.value) {
	formRef.value.addEventListener("submit", handleSubmit);
} else {
	watch(formRef, () => {
		formRef.value?.addEventListener("submit", handleSubmit);
	});
}

function handleSubmit(e: Event) {
	emit("form-submit", e as Event & { target: HTMLFormElement }, { errorMessage: errorMessage.value });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass(["shared-textfield relative grid items-center", { 'is-invalid': _unref(isValidationVisible), 'is-readonly': _ctx.readonly, 'is-disabled': _ctx.disabled }])
    }, [
      _createElementVNode("label", {
        class: "shared-textfield-label ocm-body1 relative z-10 mx-3 -mb-3 w-fit bg-white px-1",
        for: _ctx.name
      }, [
        _createTextVNode(_toDisplayString(_ctx.label), 1),
        (_ctx.required && _ctx.requiredFlag)
          ? (_openBlock(), _createElementBlock("span", _hoisted_3, "*"))
          : _createCommentVNode("", true)
      ], 8, _hoisted_2),
      _createElementVNode("input", _normalizeProps({
        id: _ctx.name,
        ref_key: "inputRef",
        ref: inputRef,
        "aria-describedby": ariaDescribedBy.value,
        autocomplete: _ctx.autocomplete,
        autocapitalize: _ctx.autocapitalize,
        autocorrect: _ctx.autocorrect,
        class: ["shared-textfield-input col-start-1 row-start-2 h-14 rounded text-base", { 'has-currency': _ctx.currency }],
        [_ctx.dataTestName || ""]: '',
        disabled: _ctx.disabled,
        inputmode: _ctx.inputmode,
        max: _ctx.max,
        maxlength: _ctx.maxlength,
        min: _ctx.min,
        name: _ctx.name,
        pattern: localPattern.value,
        placeholder: _ctx.placeholder,
        readonly: _ctx.readonly,
        required: _ctx.required,
        spellcheck: props.type === 'email' ? 'false' : props.spellcheck,
        title: "",
        type: localType.value,
        value: _unref(inputValue),
        "data-currency": _ctx.currency,
        onFocus: onFocus,
        onInput: onInput,
        onChange: onChange,
        onBlur: onBlur
      }), null, 16, _hoisted_4),
      (_ctx.currency)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_ctx.currency), 1))
        : _createCommentVNode("", true),
      (_ctx.type === 'password')
        ? (_openBlock(), _createBlock(SharedButtonOnlyIcon, {
            key: 1,
            class: "col-start-1 row-start-2 mr-1.5 size-10 justify-self-end focus-visible:outline-current",
            "icon-name": isPasswordVisible.value ? 'visibility-off' : 'visibility',
            "icon-size": "5",
            disabled: _ctx.disabled,
            onClick: togglePassword
          }, null, 8, ["icon-name", "disabled"]))
        : _createCommentVNode("", true)
    ], 2),
    (isHintVisible.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          id: hintId,
          class: "mt-1 text-xs text-gray-80"
        }, [
          _renderSlot(_ctx.$slots, "default")
        ]))
      : _createCommentVNode("", true),
    (_unref(isValidationVisible))
      ? (_openBlock(), _createElementBlock("div", _normalizeProps({
          key: 1,
          id: validationId,
          class: "on-show-slide-in mt-1 grid grid-cols-[min-content,1fr] gap-x-1 text-xs text-error",
          [dataTestErrorName.value || ""]: '',
          "aria-live": "polite"
        }), [
          _createVNode(SvgIcon, {
            class: "size-4",
            "icon-name": "error",
            title: "Fehler:"
          }),
          _createTextVNode(" " + _toDisplayString(_unref(errorMessage)), 1)
        ], 16))
      : _createCommentVNode("", true)
  ]))
}
}

})